// The caller supplies Vue Router's once-decoded query value, never a URL origin.
const hasUnsafeCharacters = (value: string) =>
  value.includes('\\') ||
  Array.from(value).some(character => {
    const code = character.charCodeAt(0)
    return code < 32 || code === 127
  })

export const streamingOrigin = (configuration: unknown): string | null => {
  if (
    typeof configuration !== 'string' ||
    !configuration ||
    configuration !== configuration.trim() ||
    hasUnsafeCharacters(configuration)
  )
    return null
  // Existing environments configure a bare host. Keep their HTTPS convention.
  let supplied = ''
  if (/^https?:\/\//i.test(configuration)) supplied = configuration
  else if (/^[\w.-]+(?::\d+)?\/?$/.test(configuration)) supplied = `https://${configuration}`
  try {
    const url = new URL(supplied)
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null
    return url.origin
  } catch {
    return null
  }
}

export const resolveCheckoutReturn = (target: unknown, configuration: unknown): string | null => {
  if (
    typeof target !== 'string' ||
    !target ||
    hasUnsafeCharacters(target) ||
    /%(?![\da-f]{2})/i.test(target) ||
    /%(?:0[\da-f]|1[\da-f]|7f|5c)/i.test(target)
  )
    return null

  const pathname = target.split(/[?#]/, 1)[0]
  // Both canonical and learning-area routes are registered Course/LP details.
  const contentRoute =
    /^\/(?:course\/[^/]+\/[^/]+|path\/[^/]+|[^/]+\/(?:courses|learning-paths)\/[^/]+\/[^/]+)\/?$/
  if (!contentRoute.test(pathname)) return null
  const origin = streamingOrigin(configuration)
  if (!origin) return null
  try {
    // Check escape validity without using decoded query/hash to validate routes.
    // Segment decoding is validation only; never recursively decode the target.
    decodeURIComponent(target)
    const safePath = pathname
      .split('/')
      .slice(1)
      .every(segment => {
        const decoded = decodeURIComponent(segment)
        return (
          !['.', '..'].includes(decoded) &&
          !/[%/\\?#]/.test(decoded) &&
          !hasUnsafeCharacters(decoded)
        )
      })
    if (!safePath) return null
    const url = new URL(target, origin)
    return url.origin === origin && !url.username && !url.password ? url.href : null
  } catch {
    return null
  }
}

export const withoutCheckoutReturn = <T extends Record<string, unknown>>(query: T) => {
  const { return_to, ...remaining } = query
  return remaining
}
